'use client';

import { useEffect, useRef } from 'react';

import useStyles from './styles';

// The runtime (EmbeddedRuntimeProvider) mounts the real miniapp into this fixed
// container at its own root and feeds it the host pubsub (auth/token/credits).
// We can't tell the runtime to mount elsewhere, so on the preset detail page we
// relocate the mounted node into this component's grid slot, then return it on
// unmount. While it waits in `#miniapp-container` on every other route,
// globals.css keeps it hidden there.
const RUNTIME_CONTAINER_ID = 'miniapp-container';

/**
 * Puts `genai-studio-v2` in the preset page's content slot — the whole app
 * (`./Embed`, which is `<App showHeader={false} />`), not a card: preview,
 * inputs, Generate, the catalogue and the user's own creations.
 *
 * The module is loaded and bootstrapped by the runtime, with the host
 * auth/pubsub bridge; this component only moves the runtime-mounted DOM node
 * into place.
 */
const MiniappEmbed = () => {
  const classes = useStyles();
  const slotRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const slot = slotRef.current;
    if (!slot) return undefined;

    // The SDK appends a single plain (non-React) child to #miniapp-container and
    // mounts the miniapp inside it. That node is safe to re-parent; the SDK's
    // own cleanup guards removal with `.contains`, so moving it is harmless.
    const moved: HTMLElement[] = [];
    let bodyObserver: MutationObserver | null = null;
    let containerObserver: MutationObserver | null = null;

    /**
     * Moves the gen.ai miniapp's root from the runtime container into the slot.
     *
     * Only that one root. Anything the runtime mounts once it is here is a
     * miniapp it opened — the share sheet, the Drive chooser — and stays in the
     * container, as it does in miniapp-shell: moved into the slot, the share
     * sheet came up blank and dead. globals.css shows those roots full-screen.
     *
     * The root with content is the one: a miniapp that closed leaves its root
     * behind empty, and this one is handed back to the end of the container
     * when the page unmounts.
     */
    const relocate = (): void => {
      if (moved.some(node => slot.contains(node))) return;
      const runtimeContainer = document.getElementById(RUNTIME_CONTAINER_ID);
      if (!runtimeContainer || runtimeContainer.childElementCount === 0) return;
      const roots = Array.from(runtimeContainer.children) as HTMLElement[];
      const root = roots.find(node => node.childElementCount > 0) ?? roots[roots.length - 1];
      slot.appendChild(root);
      moved.push(root);
    };

    /**
     * Keep watching the runtime container for the rest of this page's life.
     *
     * Relocating once was the bug behind "the preset page is blank until you
     * reload" after signing in: the SSO return lands on `/callback`, this
     * component mounts as the router replaces the route, and the runtime mounts
     * the miniapp *after* that — so the node arrived in `#miniapp-container`
     * once nobody was watching any more, and that container is
     * `display: none` site-wide. The runtime owns when it mounts, and it is
     * entitled to do so more than once; the only safe assumption is that it can
     * happen at any time.
     */
    const watchContainer = (runtimeContainer: HTMLElement): void => {
      if (containerObserver) return;
      containerObserver = new MutationObserver(relocate);
      containerObserver.observe(runtimeContainer, { childList: true });
    };

    const attach = (): void => {
      const runtimeContainer = document.getElementById(RUNTIME_CONTAINER_ID);
      if (!runtimeContainer) return;
      relocate();
      watchContainer(runtimeContainer);
      // The container exists now, so the coarse watch has done its job — the
      // fine-grained one above is scoped to a single element instead of every
      // mutation the miniapp makes.
      bodyObserver?.disconnect();
      bodyObserver = null;
    };

    attach();
    if (!containerObserver) {
      // The container is created asynchronously, after Module Federation
      // resolves, so watch for it to appear and then hand over.
      bodyObserver = new MutationObserver(attach);
      bodyObserver.observe(document.body, { childList: true, subtree: true });
    }

    return () => {
      bodyObserver?.disconnect();
      containerObserver?.disconnect();
      // Hand the node back to the (hidden) runtime container so it survives
      // navigation and doesn't show on the next route.
      const runtimeContainer = document.getElementById(RUNTIME_CONTAINER_ID);
      if (runtimeContainer) {
        moved.forEach(node => {
          if (!runtimeContainer.contains(node)) runtimeContainer.appendChild(node);
        });
      }
    };
  }, []);

  return <div ref={slotRef} className={classes.container} />;
};

export default MiniappEmbed;
